<!-- Friends — your pager card, pending requests, and everyone who can page you.
     Screens role. -->
<template>
  <IonPage>
    <AppHeader title="Friends" back="/tabs/pager">
      <IonButtons slot="end">
        <IonButton ref="addBtn" @click="addOpen = true">Add</IonButton>
      </IonButtons>
    </AppHeader>

    <IonContent :fullscreen="true" class="pm-scr-content">
      <div class="pm-scr-pad pm-scr-friends">
        <!-- Your pager card -->
        <section class="pm-scr-card" :aria-labelledby="`${uid}-friends-yours`">
          <h2 :id="`${uid}-friends-yours`" class="section-title sr-only">Your pager</h2>
          <div class="pm-scr-lcd" :class="glassClass">
            <p class="pm-scr-lcd-line">YOUR # {{ myNumber }}</p>
            <p v-if="me?.handle" class="pm-scr-lcd-line pm-scr-lcd-line--s">NAME: {{ me.handle }}</p>
          </div>

          <div class="pm-scr-field">
            <label class="pm-scr-label" :for="`${uid}-friends-handle`">Your name</label>
            <input
              :id="`${uid}-friends-handle`"
              v-model="handle"
              class="pm-scr-input pm-scr-input--lcd pm-scr-upper"
              type="text"
              maxlength="12"
              autocomplete="off"
              autocapitalize="characters"
              enterkeyhint="done"
              placeholder="WHAT SHOULD FRIENDS SEE?"
              :aria-describedby="`${uid}-friends-handle-help`"
              @input="handle = handle.toUpperCase().replace(/[^A-Z0-9 ._-]/g, '')"
              @blur="saveHandle.run()"
              @keydown.enter.prevent="$event.target.blur()"
            >
            <p :id="`${uid}-friends-handle-help`" class="pm-scr-muted pm-scr-small">Friends see this instead of your number — on their pager, their list and every page you send. Up to 12 letters.</p>
            <p v-if="saveHandle.error.value" class="field-error" role="alert">{{ saveHandle.error.value }}</p>
          </div>

          <div class="pm-scr-stack">
            <IonButton expand="block" :disabled="share.busy.value" @click="share.run()">Share invite link</IonButton>
            <div class="pm-scr-button-row">
              <IonButton fill="outline" @click="copyNumber.run()">Copy number</IonButton>
              <IonButton
                v-if="qrPath"
                fill="outline"
                :aria-expanded="qrOpen ? 'true' : 'false'"
                :aria-controls="`${uid}-friends-qr`"
                @click="qrOpen = !qrOpen"
              >
                {{ qrOpen ? 'Hide QR code' : 'Show QR code' }}
              </IonButton>
            </div>
            <p v-if="share.error.value || copyNumber.error.value" class="field-error" role="alert">{{ share.error.value || copyNumber.error.value }}</p>
          </div>

          <!-- The QR is for a friend standing next to you, so it waits behind
               a button instead of filling the first screen of Friends. -->
          <Transition name="pm-scr-reveal">
            <figure v-if="qrPath && qrOpen" :id="`${uid}-friends-qr`" class="pm-scr-qr">
              <svg :viewBox="`0 0 ${qrSize} ${qrSize}`" role="img" aria-label="QR code of your invite link" shape-rendering="crispEdges">
                <rect :width="qrSize" :height="qrSize" class="pm-scr-qr-bg" />
                <path :d="qrPath" class="pm-scr-qr-ink" />
              </svg>
              <figcaption class="pm-scr-muted pm-scr-small">A friend scans this with their phone's camera to pair.</figcaption>
            </figure>
          </Transition>
        </section>

        <div class="pm-scr-friends-col">
          <!-- Pending -->
          <section v-if="pendingIn.length || pendingOut.length" :aria-labelledby="`${uid}-friends-pending`">
            <h2 :id="`${uid}-friends-pending`" class="section-title">Pending</h2>
            <ul class="pm-scr-rows">
              <li v-for="f in pendingIn" :key="f.number" class="pm-scr-request">
                <div class="pm-scr-request-head">
                  <LcdChip :text="chip(f)" />
                  <div class="pm-scr-row-text">
                    <strong>{{ friendName(f.number) }}</strong>
                    <span class="pm-scr-muted pm-scr-small pm-scr-block">
                      <template v-if="friendSecondary(f.number)">{{ friendSecondary(f.number) }} · </template>Wants to page you
                    </span>
                  </div>
                </div>
                <div v-if="f.firstPage" class="pm-scr-bubble pm-scr-bubble--in" :class="glassClass">
                  <PageText :body="f.firstPage" />
                </div>
                <div class="pm-scr-button-row">
                  <IonButton :disabled="busyFor === f.number" @click="accept(f)">Accept</IonButton>
                  <IonButton fill="outline" color="danger" :disabled="busyFor === f.number" @click="confirmBlock(f)">Block</IonButton>
                </div>
                <p v-if="errorFor[f.number]" class="field-error" role="alert">{{ errorFor[f.number] }}</p>
              </li>
              <li v-for="f in pendingOut" :key="f.number" class="pm-scr-row">
                <div class="pm-scr-row-main pm-scr-row-static">
                  <LcdChip :text="chip(f)" />
                  <span class="pm-scr-row-text">
                    {{ friendName(f.number) }}
                    <span class="pm-scr-muted pm-scr-small pm-scr-block"><template v-if="friendSecondary(f.number)">{{ friendSecondary(f.number) }} · </template>Waiting</span>
                  </span>
                </div>
                <IonButton fill="clear" :disabled="busyFor === f.number" @click="cancel(f)">Cancel</IonButton>
              </li>
            </ul>
          </section>

          <!-- Groups: one page to a few friends at once -->
          <section v-if="!onlyOperator || groups.length" :aria-labelledby="`${uid}-friends-groups`">
            <div class="pm-scr-section-head">
              <h2 :id="`${uid}-friends-groups`" class="section-title">Groups</h2>
              <IonButton fill="clear" size="small" @click="groupOpen = true">New group</IonButton>
            </div>
            <ul v-if="groups.length" class="pm-scr-rows">
              <li v-for="g in groups" :key="g.id" class="pm-scr-row">
                <button type="button" class="pm-scr-row-main" @click="router.push(`/tabs/group/${g.id}`)">
                  <LcdChip :text="chipText(g.name)" />
                  <span class="pm-scr-row-text">
                    {{ g.name }}
                    <span class="pm-scr-muted pm-scr-small pm-scr-block">{{ groupLine(g) }}</span>
                  </span>
                </button>
              </li>
            </ul>
            <p v-else class="pm-scr-muted pm-scr-small">Page a few friends at once — make a group like CREW or FAMILY.</p>
          </section>

          <!-- Friends -->
          <section :aria-labelledby="`${uid}-friends-list`">
            <h2 :id="`${uid}-friends-list`" class="section-title">Friends</h2>
            <ul class="pm-scr-rows">
              <li v-for="f in acceptedFriends" :key="f.number" class="pm-scr-row">
                <button type="button" class="pm-scr-row-main" @click="router.push(`/tabs/thread/${f.number}`)">
                  <LcdChip :text="chip(f)" />
                  <span class="pm-scr-row-text">
                    {{ friendName(f.number) }}
                    <span v-if="f.system" class="pm-scr-tag">BUILT IN</span>
                    <span v-if="friendSecondary(f.number) || f.lastPageAt" class="pm-scr-muted pm-scr-small pm-scr-block">
                      {{ [friendSecondary(f.number), f.lastPageAt ? when(f.lastPageAt) : ''].filter(Boolean).join(' · ') }}
                    </span>
                  </span>
                </button>
                <button
                  v-if="!f.system"
                  type="button"
                  class="pm-scr-more"
                  :aria-label="`More for ${friendName(f.number)}`"
                  @click="openMenu(f, $event)"
                >
                  <IonIcon :icon="ellipsisHorizontal" aria-hidden="true" />
                </button>
              </li>
            </ul>

            <div v-if="onlyOperator" class="pm-scr-empty">
              <p>Your pager works — now it needs a friend. Share your invite link or add a number.</p>
              <IonButton @click="share.run()">Share invite link</IonButton>
            </div>
          </section>
        </div>
      </div>
    </IonContent>

    <AddFriendSheet :is-open="addOpen" @close="addOpen = false" @share="share.run()" />
    <NewGroupSheet :is-open="groupOpen" @close="groupOpen = false" @created="(id) => router.push(`/tabs/group/${id}`)" />
    <ActivationCard :is-open="cardOpen" @close="cardOpen = false" />
  </IonPage>
</template>

<script setup>
import { userMessage } from '../components/user-message.js';
import { ref, reactive, computed, watch, useId } from 'vue';
import { useRouter } from 'vue-router';
import {
  IonPage, IonContent, IonButtons, IonButton, IonIcon, actionSheetController, alertController, onIonViewWillEnter,
} from '@ionic/vue';
import { ellipsisHorizontal } from 'ionicons/icons';
import { AppHeader, useAction, toast } from '../core/ui/index.js';
import { haptic } from '../core/util.js';
import { shareLink } from '../core/export.js';
import {
  me, settings, currentDevice, acceptedFriends, pendingIn, pendingOut, inviteLink, activationCardDue, groups,
  friendName, friendSecondary, setHandle, acceptFriend, blockFriend, removeFriend, cancelRequest, refreshInbox,
} from '../store.js';
import { formatNumber, chipText } from '../pm/format.js';
import { promptName } from '../components/name-prompt.js';
import NewGroupSheet from '../components/NewGroupSheet.vue';
import { whenLong } from '../components/when.js';
import PageText from '../components/PageText.vue';
import LcdChip from '../components/LcdChip.vue';
import AddFriendSheet from '../components/AddFriendSheet.vue';
import ActivationCard from '../components/ActivationCard.vue';

// Per-instance ids: Ionic keeps earlier pages in the DOM, so a fixed id
// would repeat and break label/for pairing.
const uid = `pm${useId()}`;

const router = useRouter();

const addOpen = ref(false);
const groupOpen = ref(false);
const qrOpen = ref(false);
const cardOpen = ref(false);

const myNumber = computed(() => (me.value?.number ? formatNumber(me.value.number) : '———'));
const onlyOperator = computed(() => acceptedFriends.value.every((f) => f.system));
const glassClass = computed(() => (settings.highContrast ? 'pm-scr-glass--hc' : `pm-scr-glass--${currentDevice.value?.lcd || 'classic'}`));

watch(activationCardDue, (due) => { if (due) cardOpen.value = true; }, { immediate: true });
// Fresh names, requests and groups whenever the list is looked at.
onIonViewWillEnter(() => { refreshInbox(); });

const chip = (f) => chipText(friendName(f.number));

/** "3 pagers · You, Sam, Jess" */
function groupLine(g) {
  const names = g.members.map((m) => (m.number === me.value?.number ? 'You' : friendName(m.number)));
  return `${g.members.length} pagers · ${names.join(', ')}`;
}
const when = (ts) => whenLong(ts);

// ── your card ───────────────────────────────────────────────────────────
const handle = ref(me.value?.handle || '');
watch(() => me.value?.handle, (h) => { if (document.activeElement?.id !== `${uid}-friends-handle`) handle.value = h || ''; });

const saveHandle = useAction(async () => {
  const h = handle.value.trim().toUpperCase();
  if (h === (me.value?.handle || '')) return;
  const r = await setHandle(h);
  if (r && r.ok === false) throw new Error(userMessage(r, "Couldn't save your name. Try again."));
}, { done: 'Name saved' });

const share = useAction(async () => {
  if (!inviteLink.value) throw new Error('Your invite link is not ready yet — your pager needs a connection first.');
  await shareLink(inviteLink.value, 'Page me', `Page me on Page Me: ${myNumber.value}`);
});

const copyNumber = useAction(async () => {
  await navigator.clipboard.writeText(myNumber.value);
}, { done: 'Number copied' });

// ── QR (SVG) ────────────────────────────────────────────────────────────
const qr = computed(() => (inviteLink.value ? encodeQr(inviteLink.value) : null));
const qrSize = computed(() => (qr.value ? qr.value.length + 8 : 0));
const qrPath = computed(() => {
  const m = qr.value;
  if (!m) return '';
  let d = '';
  for (let y = 0; y < m.length; y++) {
    for (let x = 0; x < m.length; x++) if (m[y][x]) d += `M${x + 4} ${y + 4}h1v1h-1z`;
  }
  return d;
});

// ── pending ─────────────────────────────────────────────────────────────
const busyFor = ref('');
const errorFor = reactive({});
async function act(f, fn, fallback) {
  if (busyFor.value) return false;
  busyFor.value = f.number;
  errorFor[f.number] = '';
  try {
    const r = await fn(f.number);
    if (r && r.ok === false) throw new Error(userMessage(r, fallback));
    return true;
  } catch (e) {
    errorFor[f.number] = e?.message || fallback;
    return false;
  } finally {
    busyFor.value = '';
  }
}
// Accepting is the moment to save the number as someone.
async function accept(f) {
  if (!(await act(f, acceptFriend, "Couldn't accept. Try again."))) return;
  await promptName(f.number, {
    header: 'Save them as',
    message: `You and ${friendName(f.number)} can page each other now. What should they be called on this phone?`,
  });
}
const cancel = (f) => act(f, cancelRequest, "Couldn't cancel. Try again.");

async function confirmBlock(f) {
  const who = friendName(f.number);
  const a = await alertController.create({
    header: `Block ${who}?`,
    message: "They won't be able to page you or send another request. They aren't told.",
    buttons: [{ text: 'Cancel', role: 'cancel' }, { text: 'Block', role: 'destructive' }],
  });
  await a.present();
  const { role } = await a.onDidDismiss();
  if (role !== 'destructive') return;
  // The row leaves the list, so say where it went — and that it can come back.
  haptic('pop');
  if (await act(f, blockFriend, "Couldn't block. Try again.")) {
    toast(`${who} blocked. Unblock in Settings.`);
  }
}

// ── friend ⋯ menu ───────────────────────────────────────────────────────
async function openMenu(f, event) {
  const trigger = event?.currentTarget;
  const who = friendName(f.number);
  const sheet = await actionSheetController.create({
    header: who,
    buttons: [
      { text: 'Rename', data: 'rename' },
      { text: 'Block', role: 'destructive', data: 'block' },
      { text: 'Remove', role: 'destructive', data: 'remove' },
      { text: 'Cancel', role: 'cancel' },
    ],
  });
  await sheet.present();
  const { data } = await sheet.onDidDismiss();
  const choice = data?.action ?? data;

  if (choice === 'rename') {
    await promptName(f.number);
  } else if (choice === 'block') {
    await confirmBlock(f);
  } else if (choice === 'remove') {
    const a = await alertController.create({
      header: `Remove ${who}?`,
      message: "You'll stop getting their pages. To page each other again, one of you has to add the other.",
      buttons: [{ text: 'Cancel', role: 'cancel' }, { text: 'Remove', role: 'destructive' }],
    });
    await a.present();
    const { role } = await a.onDidDismiss();
    if (role === 'destructive') await act(f, removeFriend, "Couldn't remove. Try again.");
  }
  try { trigger?.focus?.(); } catch { /* gone */ }
}

/* ────────────────────────────────────────────────────────────────────────
 * A tiny QR encoder: byte mode, error correction M, versions 1–10, which
 * covers any invite link. Follows ISO/IEC 18004 (and the structure of
 * Nayuki's reference implementation). Returns a boolean matrix [y][x].
 * No dependency on purpose — the app renders its QR offline.
 * ──────────────────────────────────────────────────────────────────────── */
function encodeQr(text) {
  const bytes = [...new globalThis.TextEncoder().encode(text)];
  const ECC = [0, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26];
  const BLOCKS = [0, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5];
  const rawModules = (v) => {
    let r = (16 * v + 128) * v + 64;
    if (v >= 2) { const na = Math.floor(v / 7) + 2; r -= (25 * na - 10) * na - 55; if (v >= 7) r -= 36; }
    return r;
  };
  const dataCap = (v) => Math.floor(rawModules(v) / 8) - ECC[v] * BLOCKS[v];

  let ver = 1;
  for (; ver <= 10; ver++) {
    const bits = 4 + (ver < 10 ? 8 : 16) + bytes.length * 8;
    if (bits <= dataCap(ver) * 8) break;
  }
  if (ver > 10) return null;

  // Data bits
  const bb = [];
  const put = (val, len) => { for (let i = len - 1; i >= 0; i--) bb.push((val >>> i) & 1); };
  put(4, 4);
  put(bytes.length, ver < 10 ? 8 : 16);
  bytes.forEach((b) => put(b, 8));
  const capBits = dataCap(ver) * 8;
  put(0, Math.min(4, capBits - bb.length));
  put(0, (8 - (bb.length % 8)) % 8);
  for (let pad = 0xEC; bb.length < capBits; pad ^= 0xEC ^ 0x11) put(pad, 8);
  const data = [];
  for (let i = 0; i < bb.length; i += 8) data.push(parseInt(bb.slice(i, i + 8).join(''), 2));

  // Reed–Solomon
  const mul = (x, y) => { let z = 0; for (let i = 7; i >= 0; i--) { z = (z << 1) ^ ((z >>> 7) * 0x11D); z ^= ((y >>> i) & 1) * x; } return z; };
  const divisor = (deg) => {
    const r = new Array(deg).fill(0); r[deg - 1] = 1; let root = 1;
    for (let i = 0; i < deg; i++) {
      for (let j = 0; j < r.length; j++) { r[j] = mul(r[j], root); if (j + 1 < r.length) r[j] ^= r[j + 1]; }
      root = mul(root, 0x02);
    }
    return r;
  };
  const remainder = (dat, div) => {
    const r = div.map(() => 0);
    for (const b of dat) { const f = b ^ r.shift(); r.push(0); div.forEach((c, i) => { r[i] ^= mul(c, f); }); }
    return r;
  };
  const nb = BLOCKS[ver];
  const eccLen = ECC[ver];
  const raw = Math.floor(rawModules(ver) / 8);
  const numShort = nb - (raw % nb);
  const shortLen = Math.floor(raw / nb);
  const div = divisor(eccLen);
  const blocks = [];
  for (let i = 0, k = 0; i < nb; i++) {
    const dat = data.slice(k, k + shortLen - eccLen + (i < numShort ? 0 : 1));
    k += dat.length;
    const ecc = remainder(dat, div);
    if (i < numShort) dat.push(0);
    blocks.push(dat.concat(ecc));
  }
  const codewords = [];
  for (let i = 0; i < blocks[0].length; i++) {
    blocks.forEach((b, j) => { if (i !== shortLen - eccLen || j >= numShort) codewords.push(b[i]); });
  }

  // Matrix
  const size = ver * 4 + 17;
  const mod = Array.from({ length: size }, () => new Array(size).fill(false));
  const fn = Array.from({ length: size }, () => new Array(size).fill(false));
  const set = (x, y, dark) => { mod[y][x] = dark; fn[y][x] = true; };
  const bit = (v, i) => ((v >>> i) & 1) !== 0;

  for (let i = 0; i < size; i++) { set(6, i, i % 2 === 0); set(i, 6, i % 2 === 0); }
  const finder = (cx, cy) => {
    for (let dy = -4; dy <= 4; dy++) for (let dx = -4; dx <= 4; dx++) {
      const x = cx + dx; const y = cy + dy;
      const dist = Math.max(Math.abs(dx), Math.abs(dy));
      if (x >= 0 && x < size && y >= 0 && y < size) set(x, y, dist !== 2 && dist !== 4);
    }
  };
  finder(3, 3); finder(size - 4, 3); finder(3, size - 4);
  if (ver > 1) {
    const n = Math.floor(ver / 7) + 2;
    const step = Math.ceil((ver * 4 + 4) / (n * 2 - 2)) * 2;
    const pos = [6];
    for (let p = ver * 4 + 10; pos.length < n; p -= step) pos.splice(1, 0, p);
    for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
      if ((i === 0 && j === 0) || (i === 0 && j === n - 1) || (i === n - 1 && j === 0)) continue;
      for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {
        set(pos[i] + dx, pos[j] + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
      }
    }
  }
  const format = (mask) => {
    const d = (0 << 3) | mask; // ECC level M = 0b00
    let rem = d;
    for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    const bits = ((d << 10) | rem) ^ 0x5412;
    for (let i = 0; i <= 5; i++) set(8, i, bit(bits, i));
    set(8, 7, bit(bits, 6)); set(8, 8, bit(bits, 7)); set(7, 8, bit(bits, 8));
    for (let i = 9; i < 15; i++) set(14 - i, 8, bit(bits, i));
    for (let i = 0; i < 8; i++) set(size - 1 - i, 8, bit(bits, i));
    for (let i = 8; i < 15; i++) set(8, size - 15 + i, bit(bits, i));
    set(8, size - 8, true);
  };
  format(0);
  if (ver >= 7) {
    let rem = ver;
    for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1F25);
    const bits = (ver << 12) | rem;
    for (let i = 0; i < 18; i++) {
      const a = size - 11 + (i % 3); const b = Math.floor(i / 3);
      set(a, b, bit(bits, i)); set(b, a, bit(bits, i));
    }
  }
  // Codewords, zig-zag
  let i = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const up = ((right + 1) & 2) === 0;
        const y = up ? size - 1 - vert : vert;
        if (!fn[y][x] && i < codewords.length * 8) { mod[y][x] = bit(codewords[i >>> 3], 7 - (i & 7)); i++; }
      }
    }
  }
  const MASKS = [
    (x, y) => (x + y) % 2 === 0, (x, y) => y % 2 === 0, (x) => x % 3 === 0, (x, y) => (x + y) % 3 === 0,
    (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0, (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
    (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0, (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
  ];
  const applyMask = (m) => {
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) if (!fn[y][x] && MASKS[m](x, y)) mod[y][x] = !mod[y][x];
  };
  // Simplified penalty (runs, 2×2 blocks, balance) — any mask is valid; this
  // only picks a more readable one.
  const penalty = () => {
    let p = 0; let dark = 0;
    for (let a = 0; a < size; a++) {
      let rr = 1; let rc = 1;
      for (let b = 0; b < size; b++) {
        if (mod[a][b]) dark++;
        if (b > 0) {
          if (mod[a][b] === mod[a][b - 1]) { rr++; if (rr === 5) p += 3; else if (rr > 5) p++; } else rr = 1;
          if (mod[b][a] === mod[b - 1][a]) { rc++; if (rc === 5) p += 3; else if (rc > 5) p++; } else rc = 1;
        }
        if (a > 0 && b > 0) {
          const c = mod[a][b];
          if (c === mod[a - 1][b] && c === mod[a][b - 1] && c === mod[a - 1][b - 1]) p += 3;
        }
      }
    }
    const total = size * size;
    p += (Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1) * 10;
    return p;
  };
  let best = 0; let bestScore = Infinity;
  for (let m = 0; m < 8; m++) {
    applyMask(m); format(m);
    const s = penalty();
    if (s < bestScore) { best = m; bestScore = s; }
    applyMask(m);
  }
  applyMask(best); format(best);
  return mod;
}
</script>
